<template>
  <div>
    <!-- Filters -->
    <div class="field is-grouped mt-3">
      <div class="control">
        <input v-model="filters.start_date" class="input is-small" type="datetime-local" placeholder="Start Date">
      </div>
      <div class="control">
        <input v-model="filters.end_date" class="input is-small" type="datetime-local" placeholder="End Date">
      </div>
      <div class="control">
        <select v-model="filters.level" class="input is-small">
          <option value="">All Levels</option>
          <option value="DEBUG">DEBUG</option>
          <option value="INFO">INFO</option>
          <option value="WARNING">WARNING</option>
          <option value="ERROR">ERROR</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>
      </div>
      <div class="control">
        <input v-model="filters.logger_name" class="input is-small" type="text" placeholder="Logger Name">
      </div>
      <div class="control">
        <input v-model="filters.module" class="input is-small" type="text" placeholder="Module">
      </div>
      <div class="control">
        <input v-model="filters.function_name" class="input is-small" type="text" placeholder="Function">
      </div>
      <div class="control">
        <button class="button is-primary is-small" @click="applyFilters">Apply Filters</button>
      </div>
      <div class="control">
        <button class="button is-warning is-small" @click="resetFilters">Reset Filters</button>
      </div>
      <div class="control">
        <button class="button is-success is-small" @click="downloadCSV">Download CSV</button>
      </div>
    </div>

    <!-- Search -->
    <div class="field mt-3">
      <div class="control">
        <input v-model="searchQuery" class="input" type="text" placeholder="Search in log messages..." @input="debounceSearch">
      </div>
    </div>

    <!-- Auto Refresh Toggle -->
    <div class="field mt-3">
      <div class="control">
        <label class="checkbox">
          <input type="checkbox" v-model="autoRefresh" @change="toggleAutoRefresh">
          Auto Refresh (30s)
        </label>
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="loading" class="has-text-centered mt-5">
      <div class="loader"></div>
      <p>Loading logs...</p>
    </div>

    <!-- Logs Table -->
    <div v-else class="table-container mt-5">
      <table class="table is-fullwidth is-small is-striped">
        <thead>
          <tr>
            <th class="is-size-7">Timestamp</th>
            <th class="is-size-7">Level</th>
            <th class="is-size-7">Logger</th>
            <th class="is-size-7">Module</th>
            <th class="is-size-7">Function</th>
            <th class="is-size-7">Line</th>
            <th class="is-size-7">Message</th>
            <th class="is-size-7">UUID</th>
            <th class="is-size-7">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logs" :key="log.id" :class="getLogRowClass(log.level)">
            <td class="is-size-7">{{ formatDateTime(log.timestamp) }}</td>
            <td class="is-size-7">
              <span class="tag" :class="getLevelTagClass(log.level)">{{ log.level }}</span>
            </td>
            <td class="is-size-7">{{ log.logger_name }}</td>
            <td class="is-size-7">{{ log.module }}</td>
            <td class="is-size-7">{{ log.function_name }}</td>
            <td class="is-size-7">{{ log.line_number }}</td>
            <td class="is-size-7">
              <span :title="log.message" class="log-message">
                {{ truncateMessage(log.message) }}
              </span>
            </td>
            <td class="is-size-7">
              <code class="is-size-7">{{ log.uuid }}</code>
            </td>
            <td class="is-size-7">
              <button class="button is-small is-info" @click="viewFullMessage(log)">
                <font-awesome-icon icon="eye" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Pagination -->
      <nav class="pagination is-centered" role="navigation" aria-label="pagination">
        <button 
          class="pagination-previous button" 
          :disabled="!previousPage" 
          @click="loadPreviousPage"
        >
          Previous
        </button>
        <button 
          class="pagination-next button" 
          :disabled="!nextPage" 
          @click="loadNextPage"
        >
          Next page
        </button>
        <span class="pagination-info">Page {{ currentPage }}</span>
      </nav>
    </div>

    <!-- Message Modal -->
    <div class="modal" :class="{ 'is-active': showMessageModal }">
      <div class="modal-background" @click="closeMessageModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Log Message Details</p>
          <button class="delete" aria-label="close" @click="closeMessageModal"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="selectedLog">
            <div class="field">
              <label class="label">Timestamp:</label>
              <p>{{ formatDateTime(selectedLog.timestamp) }}</p>
            </div>
            <div class="field">
              <label class="label">Level:</label>
              <span class="tag" :class="getLevelTagClass(selectedLog.level)">{{ selectedLog.level }}</span>
            </div>
            <div class="field">
              <label class="label">Logger:</label>
              <p>{{ selectedLog.logger_name }}</p>
            </div>
            <div class="field">
              <label class="label">Module:</label>
              <p>{{ selectedLog.module }}</p>
            </div>
            <div class="field">
              <label class="label">Function:</label>
              <p>{{ selectedLog.function_name }}:{{ selectedLog.line_number }}</p>
            </div>
            <div class="field">
              <label class="label">UUID:</label>
              <code>{{ selectedLog.uuid }}</code>
            </div>
            <div class="field">
              <label class="label">Message:</label>
              <div class="box">
                <pre style="white-space: pre-wrap; word-wrap: break-word;">{{ selectedLog.message }}</pre>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="closeMessageModal">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import backendLogsApi from '@/api/backendLogsApi';
import { toast } from 'bulma-toast';

export default {
  name: 'BackendLogsContent',
  data() {
    return {
      logs: [],
      loading: false,
      filters: {
        start_date: '',
        end_date: '',
        level: '',
        logger_name: '',
        module: '',
        function_name: '',
      },
      searchQuery: '',
      searchTimeout: null,
      autoRefresh: false,
      refreshInterval: null,
      previousPage: null,
      nextPage: null,
      currentPage: 1,
      showMessageModal: false,
      selectedLog: null,
    };
  },
  methods: {
    async fetchLogs(url = null, append = false) {
      this.loading = true;
      try {
        const params = { ...this.filters };
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        const response = await backendLogsApi.fetchLogs(url ? {} : params, url);

        if (append) {
          this.logs = [...this.logs, ...response.data.results];
        } else {
          this.logs = response.data.results;
        }
        
        this.previousPage = response.data.previous;
        this.nextPage = response.data.next;
        this.currentPage = url ? this.currentPage : 1;
        
      } catch (error) {
        console.error('Error fetching logs:', error);
        toast({
          message: "Failed to load backend logs. Please try again.",
          type: "is-danger",
          duration: 5000,
          position: "top-right",
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadNextPage() {
      if (this.nextPage) {
        this.currentPage++;
        await this.fetchLogs(this.nextPage);
      }
    },
    
    async loadPreviousPage() {
      if (this.previousPage) {
        this.currentPage--;
        await this.fetchLogs(this.previousPage);
      }
    },
    
    applyFilters() {
      this.currentPage = 1;
      this.fetchLogs();
    },
    
    resetFilters() {
      this.filters = {
        start_date: '',
        end_date: '',
        level: '',
        logger_name: '',
        module: '',
        function_name: '',
      };
      this.searchQuery = '';
      this.currentPage = 1;
      this.fetchLogs();
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchLogs();
      }, 500);
    },
    
    toggleAutoRefresh() {
      if (this.autoRefresh) {
        this.refreshInterval = setInterval(() => {
          this.fetchLogs();
        }, 30000); // 30 seconds
        toast({
          message: "Auto-refresh enabled (30s intervals)",
          type: "is-info",
          duration: 3000,
          position: "top-right",
        });
      } else {
        clearInterval(this.refreshInterval);
        toast({
          message: "Auto-refresh disabled",
          type: "is-info",
          duration: 3000,
          position: "top-right",
        });
      }
    },
    
    async downloadCSV() {
      try {
        const params = { ...this.filters };
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        const response = await backendLogsApi.downloadLogsCSV(params);
        backendLogsApi.downloadCSVFile(response.data);
        
        toast({
          message: "CSV downloaded successfully.",
          type: "is-success",
          duration: 3000,
          position: "top-right",
        });
      } catch (error) {
        console.error('Error downloading CSV:', error);
        toast({
          message: "Failed to download CSV. Please try again.",
          type: "is-danger",
          duration: 5000,
          position: "top-right",
        });
      }
    },
    
    formatDateTime(timestamp) {
      return new Date(timestamp).toLocaleString();
    },
    
    truncateMessage(message, maxLength = 80) {
      if (message.length <= maxLength) return message;
      return message.substring(0, maxLength) + '...';
    },
    
    getLevelTagClass(level) {
      const classes = {
        'DEBUG': 'is-light',
        'INFO': 'is-info',
        'WARNING': 'is-warning',
        'ERROR': 'is-danger',
        'CRITICAL': 'is-dark'
      };
      return classes[level] || 'is-light';
    },
    
    getLogRowClass(level) {
      if (level === 'ERROR' || level === 'CRITICAL') {
        return 'has-background-danger-light';
      } else if (level === 'WARNING') {
        return 'has-background-warning-light';
      }
      return '';
    },
    
    viewFullMessage(log) {
      this.selectedLog = log;
      this.showMessageModal = true;
    },
    
    closeMessageModal() {
      this.showMessageModal = false;
      this.selectedLog = null;
    },
  },
  
  async created() {
    await this.fetchLogs();
  },
  
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-container {
  overflow-x: auto;
}

.log-message {
  max-width: 300px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.log-message:hover {
  color: #3273dc;
}

.field.is-grouped {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.field.is-grouped .control {
  margin-bottom: 0.5rem;
}

pre {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
}

.pagination {
  margin-top: 2rem;
}

.pagination-info {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: #4a4a4a;
}

.checkbox {
  font-weight: normal;
}
</style>
